import { useNavigate } from 'react-router-dom'
import type { LabOrder } from '../../store/labOrdersSlice'

interface LabOrderListTableProps {
  orders: LabOrder[]
}

function formatLabOrderId(num: number): string {
  return `LAB-${String(num).padStart(4, '0')}`
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

const statusColors: Record<string, string> = {
  Ordered: 'bg-[#e0e4e8] text-status-info',
  'Sample Collected': 'bg-[#e8e0d0] text-[#8a6b30]',
  'Sent to Lab': 'bg-[#e0e4e8] text-[#5a6a8a]',
  'Results Received': 'bg-[#e8ddf0] text-[#7a5a8a]',
  Reviewed: 'bg-accent-subtle-bg text-accent-dark',
}

export function LabOrderListTable({ orders }: LabOrderListTableProps) {
  const navigate = useNavigate()

  if (orders.length === 0) {
    return (
      <div data-testid="lab-orders-table" className="flex flex-col">
        <p className="text-[12px] text-text-muted py-8 text-center">No lab orders found</p>
      </div>
    )
  }

  return (
    <div data-testid="lab-orders-table" className="flex flex-col">
      {/* Header row */}
      <div
        className="lw-table-header grid items-center h-9 text-[11px] font-medium text-text-muted uppercase tracking-wide"
        style={{
          gridTemplateColumns: '100px 1fr 1fr 1fr 130px 140px',
        }}
      >
        <span className="px-2">Lab Order ID</span>
        <span className="px-2">Patient Name</span>
        <span className="px-2">Test Name</span>
        <span className="px-2">Ordered By</span>
        <span className="px-2">Date Ordered</span>
        <span className="px-2">Status</span>
      </div>

      {/* Data rows */}
      {orders.map((order) => (
        <div
          key={order.id}
          data-testid={`lab-order-row-${order.id}`}
          onClick={() => navigate(`/lab-work/${order.id}`)}
          className="lw-table-row grid items-center h-[44px] text-[13px] text-text-primary hover:bg-hover transition-colors duration-100 cursor-pointer"
          style={{
            gridTemplateColumns: '100px 1fr 1fr 1fr 130px 140px',
          }}
        >
          <span className="px-2 font-[450]" data-testid="lab-order-id">
            {formatLabOrderId(order.lab_order_number)}
          </span>
          <span className="px-2 truncate" data-testid="lab-order-patient">
            {order.patient_name}
          </span>
          <span className="px-2 truncate" data-testid="lab-order-test">
            {order.test_name}
          </span>
          <span className="px-2 truncate text-[12px] text-text-secondary" data-testid="lab-order-vet">
            {order.vet_name || '\u2014'}
          </span>
          <span className="px-2 text-[12px] text-text-muted" data-testid="lab-order-date">
            {formatDate(order.date_ordered)}
          </span>
          <span className="px-2" data-testid="lab-order-status">
            <span
              className={`inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium ${statusColors[order.status] || 'bg-border text-text-muted'}`}
            >
              {order.status}
            </span>
          </span>
        </div>
      ))}
    </div>
  )
}
